import { equals } from "ramda";
import { type FC, type PropsWithChildren, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ZodError } from "zod";

import { createWorkflowEditorStore, WorkflowEditorContext } from "@/modules/workflow/stores/workflow-editor-store.ts";
import { debouncedSave } from "@/modules/workflow/utils/debounceSave.ts";
import { type AutoSaveData, getWorkflowEventInputForSaving } from "@/modules/workflow/utils/generateWorkflow.ts";

export const WorkflowEditorStoreProvider: FC<PropsWithChildren> = ({ children }) => {
  const [store] = useState(() => createWorkflowEditorStore());
  const intl = useIntl();
  // Starts true so the very first null -> data transition (initial load) is suppressed.
  // Re-set to true whenever data goes null because of a loading flag, NOT just isDragging.
  const shouldSkipNextDataTransitionRef = useRef(true);

  useEffect(() => {
    shouldSkipNextDataTransitionRef.current = true;

    return store.subscribe<{
      data: AutoSaveData | null;
      isLoadingWorkflow: boolean;
      isLoadingActivities: boolean;
    }>(
      (state) => {
        const data: AutoSaveData | null =
          state.isLoadingActivities || state.isLoadingWorkflow || state.isDragging
            ? null
            : [state.workflowEvent, state.nodes, state.edges, state.parametersRecord, state.sampleTypeId];

        return {
          data,
          isLoadingWorkflow: state.isLoadingWorkflow,
          isLoadingActivities: state.isLoadingActivities,
        };
      },
      async (
        selected: { data: AutoSaveData | null; isLoadingWorkflow: boolean; isLoadingActivities: boolean },
        previousSelected: {
          data: AutoSaveData | null;
          isLoadingWorkflow: boolean;
          isLoadingActivities: boolean;
        } | null,
      ) => {
        const { data, isLoadingWorkflow, isLoadingActivities } = selected;
        const previousData: AutoSaveData | null = previousSelected?.data ?? null;

        if (data == null) {
          // Mark a loading transition so the upcoming null -> data recovery is skipped.
          // We deliberately do NOT mark isDragging-only transitions — those should save on recovery.
          if (isLoadingWorkflow || isLoadingActivities) {
            shouldSkipNextDataTransitionRef.current = true;
          }
          return;
        }

        // Skip null -> data transitions that were caused by loading (initial load or event switch).
        // If previousData is null but isTransitioningFromLoadingRef is false, we're recovering
        // from isDragging = true instead - fall through so the drag-end position change is saved.
        if (previousData == null && shouldSkipNextDataTransitionRef.current) {
          shouldSkipNextDataTransitionRef.current = false;
          return;
        }

        // Skip if the workflow event changed without going through a loading state
        // (e.g. direct store update, cached event, same-tab URL navigation).
        if (previousData != null && previousData[0] != null && data[0] != null && previousData[0].id !== data[0].id) {
          return;
        }

        try {
          const currentSaveData = getWorkflowEventInputForSaving(data);

          let shouldSave: boolean;
          try {
            if (previousData == null) {
              // Recovering from isDragging only — node positions changed, can't compare, always save.
              shouldSave = true;
            } else {
              const previousSaveData = getWorkflowEventInputForSaving(previousData);
              // This is an acceptable mistake to do it like:
              // Currently we are not comparing the last saved state against the last state to save, but only the previous frame state against the last one.
              // In other words, if you, for instance, move an activity and place it at the exact same place, we should not save it after the debounce time.
              // But we do, because we are comparing with the previous frame position, not the previous saved position.
              // That's ok for now.
              shouldSave = !equals(currentSaveData, previousSaveData);
            }
          } catch {
            shouldSave = true;
          }

          if (shouldSave) {
            await debouncedSave(currentSaveData, store.setState);
          }
        } catch (error) {
          if (error instanceof ZodError) {
            store.setState({
              autoSaveErrorMessage: [...new Set(error.issues.map((error) => error.message))].join(", "),
            });
          } else if (error instanceof Error) {
            store.setState({ autoSaveErrorMessage: error.message });
          } else {
            store.setState({ autoSaveErrorMessage: intl.formatMessage({ id: "an error occurred" }) });
          }
        }
      },
      { equalityFn: equals },
    );
  }, [store, intl]);

  return <WorkflowEditorContext.Provider value={store}>{children}</WorkflowEditorContext.Provider>;
};
